import { Component, computed, Input, OnInit, signal } from '@angular/core';
import { FormControl } from '@angular/forms';
import { getClaimKey } from '../../../../functions/get-claim-key';
import { ClaimKey } from '../../../../models/api/core/claim-key';
import { FinancialOverviewResponse } from '../../../../models/api/view/claim/financials/financial-overview/financial-overview-response';
import { BaseComponent } from '../../../base/base.component';
import { CardComponent } from '../../../core/panels/card.component';
import { DetailItemComponent } from '../../../core/panels/detail-panel/detail-item.component';
import { DetailPanelComponent } from '../../../core/panels/detail-panel/detail-panel.component';
import {
  LoadingPanelComponent,
  LoadingState,
} from '../../../core/panels/loading-panel.component';
import { ColumnComponent } from '../../../core/table/column.component';
import { TableComponent } from '../../../core/table/table.component';

@Component({
  selector: 'app-overview-payment-summary-tab',
  imports: [
    CardComponent,
    TableComponent,
    ColumnComponent,
    DetailPanelComponent,
    DetailItemComponent,
    LoadingPanelComponent,
  ],
  template: `
    <app-loading-panel [state]="loadingState" loadingStyle="Box">
      <ng-template #loaded>
        <div class="tw-flex tw-flex-col sm:tw-flex-row tw-basis-full tw-gap-8">
          <div class="tw-basis-full sm:tw-basis-1/3 tw-min-w-0">
            <app-card label="Paid to date">
              <app-detail-panel [dataSource]="activePaidToDateSummary()">
                <app-detail-item
                  icon="fa-light fa-add"
                  label="+ Total paid"
                  name="paidToDateTotals"
                  labelCase="none"
                  format="c"
                />
                <app-detail-item
                  label="+ Scheduled"
                  name="scheduledTotals"
                  labelCase="none"
                  format="c"
                />
                <app-detail-item
                  label="- 1st party deductibles"
                  name="netDeductableTotals"
                  labelCase="none"
                  format="c"
                />
                <app-detail-item
                  label="- Expected receivables"
                  name="totalDeductionsExpected"
                  labelCase="none"
                  format="c"
                />
                <app-detail-item
                  label="- Recoveries"
                  name="totalRecoveries"
                  labelCase="none"
                  format="c"
                />

                <app-detail-item
                  label="- Outstanding reimbursables"
                  name="outstandingReimbursables"
                  labelCase="none"
                  format="c"
                />
                <app-detail-item
                  label="Expected cost to date"
                  name="expectedCostToDate"
                  labelCase="none"
                  format="c"
                  classes="total-row"
                />
              </app-detail-panel>
            </app-card>
          </div>
          <div class="tw-basis-full sm:tw-basis-2/3 tw-min-w-0">
            @if (!liability) {
              <app-card label="NCCI">
                <app-table
                  [dataSource]="ncciData()"
                  [pageSize]="10"
                  [scrollable]="true"
                  dataKey="category"
                  recordLabel="item"
                  [showPaginator]="false"
                  sortField="category"
                  [sortOrder]="1"
                  [globalFilterFields]="['category', 'description', 'amount']"
                >
                  <app-column name="category" label="Category" />
                  <app-column name="description" label="Description" />
                  <app-column name="amount" label="Amount" format="c" />
                </app-table>
              </app-card>
            }
          </div>
        </div>
      </ng-template>
    </app-loading-panel>
  `,
  styles: ``,
})
export class PaymentSummaryTabComponent
  extends BaseComponent
  implements OnInit
{
  @Input() level = new FormControl<string>('claim');
  @Input() liability = false;

  loadingState: LoadingState = 'Loading';

  private readonly INDEMNITY = 0;
  private readonly MEDICAL = 2;
  private readonly OTHER = 3;
  private readonly EXPENSE = 5;

  private _selectedLevel = signal<string>('claim');
  private _financialData = signal<FinancialOverviewResponse | null>(null);

  activeDataSet = computed(() => {
    const data = this._financialData();
    const currentLevel = this._selectedLevel();

    if (!data?.overview) return null;

    switch (currentLevel) {
      case 'claim':
        return data.overview.claimDataSet;
      case 'Claimant':
        return data.overview.claimantDataSet;
      case 'Event':
        return data.overview.eventDataSet;
      default:
        return data.overview.claimDataSet;
    }
  });

  ncciData = computed(() => {
    return this._financialData()?.ncciData || [];
  });

  activePaidToDateSummary = computed(() => {
    const data = this._financialData();
    const currentLevel = this._selectedLevel();
    const dataSet = this.activeDataSet();

    if (!data?.overview || !dataSet) return null;

    const paid = dataSet.paid || [0, 0, 0, 0, 0, 0];
    const scheduled = dataSet.scheduled || [0, 0, 0, 0, 0, 0];
    const deductible = dataSet.deductible || [0, 0, 0, 0, 0, 0];
    const recovery = dataSet.recovery || [0, 0, 0, 0, 0, 0];
    const deductibleExpected = dataSet.deductibleExpected || [0, 0, 0, 0, 0, 0];
    const deductibleReceived = dataSet.deductibleReceived || [0, 0, 0, 0, 0, 0];

    const sum = (arr: number[]) =>
      arr[this.INDEMNITY] +
      arr[1] +
      arr[this.MEDICAL] +
      arr[this.OTHER] +
      arr[4] +
      arr[this.EXPENSE];

    const paidToDateTotals = sum(paid);
    const scheduledTotals = sum(scheduled);
    const netDeductableTotals = sum(deductible);
    const totalRecoveries = sum(recovery);
    const totalDeductionsExpected = sum(deductibleExpected);
    const totalDeductionsReceived = sum(deductibleReceived);

    let outstandingReimbursables;

    if (currentLevel === 'claim') {
      outstandingReimbursables = data.overview.outstandingReimbursables || 0;
    } else {
      let reimbursablesList: any[] = [];
      switch (currentLevel) {
        case 'Claimant':
          reimbursablesList = data.reimbursablesClaimantLevel || [];
          break;
        case 'Event':
          reimbursablesList = data.reimbursablesEventLevel || [];
          break;
      }

      const totalAmount = reimbursablesList.reduce(
        (sum: number, r: any) => sum + r.amount,
        0,
      );
      const totalRecovered = reimbursablesList.reduce(
        (sum: number, r: any) => sum + r.recovered,
        0,
      );
      outstandingReimbursables = Math.max(0, totalAmount - totalRecovered);
    }

    const expectedCostToDate =
      paidToDateTotals -
      totalDeductionsExpected -
      totalRecoveries +
      scheduledTotals -
      outstandingReimbursables;

    return {
      paidToDateTotals,
      scheduledTotals,
      netDeductableTotals: -netDeductableTotals,
      totalRecoveries: -totalRecoveries,
      totalDeductionsExpected: -totalDeductionsExpected,
      totalDeductionsReceived,
      outstandingReimbursables: -outstandingReimbursables,
      expectedCostToDate,
    };
  });

  async ngOnInit() {
    const claimKey: ClaimKey = getClaimKey();
    this._selectedLevel.set(this.level.value || 'claim');
    this.dispose.subs = this.level.valueChanges.subscribe((value) => {
      if (value) {
        this._selectedLevel.set(value);
      }
    });
    const response =
      await this.api.financialOverview.getPaymentSummary(claimKey);
    this._financialData.set(response);
    this.loadingState = 'Loaded';
  }
}
